import { useEffect, useState, useCallback, Fragment } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import PageHeader from "@/components/app/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, X, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const TYPES = [
  { k: "risk", l: "Risk", color: "text-red-900 bg-red-200 border border-red-300" },
  { k: "assumption", l: "Assumption", color: "text-blue-800 bg-blue-100 border border-blue-200" },
  { k: "issue", l: "Issue", color: "text-orange-800 bg-orange-100 border border-orange-200" },
  { k: "dependency", l: "Dependency", color: "text-violet-800 bg-violet-100 border border-violet-200" },
];

const RaidPage = () => {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ type: "risk", title: "", description: "", impact: "medium", likelihood: "medium", status: "open", owner: "", mitigation_plan: "", mitigation_due_date: "" });
  const [expanded, setExpanded] = useState(null);
  const [mitigationDrafts, setMitigationDrafts] = useState({});
  const [noteDrafts, setNoteDrafts] = useState({});

  const load = useCallback(async () => {
    const [p, r] = await Promise.all([
      api.get(`/projects/${projectId}`),
      api.get(`/raid?project_id=${projectId}`),
    ]);
    setProject(p.data);
    setItems(r.data);
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.title.trim()) return;
    const { data } = await api.post("/raid", { project_id: projectId, ...form });
    setItems([data, ...items]);
    setOpen(false);
    setForm({ type: "risk", title: "", description: "", impact: "medium", likelihood: "medium", status: "open", owner: "", mitigation_plan: "", mitigation_due_date: "" });
    toast.success("Item added");
  };

  const updateStatus = async (id, status) => {
    setItems((p) => p.map((it) => (it.raid_id === id ? { ...it, status } : it)));
    await api.patch(`/raid/${id}`, { status });
  };

  const remove = async (id) => {
    setItems((p) => p.filter((it) => it.raid_id !== id));
    await api.delete(`/raid/${id}`);
  };

  const toggleExpand = (item) => {
    if (expanded === item.raid_id) {
      setExpanded(null);
      return;
    }
    setExpanded(item.raid_id);
    setMitigationDrafts((p) => ({
      ...p,
      [item.raid_id]: p[item.raid_id] ?? { mitigation_plan: item.mitigation_plan || "", mitigation_due_date: item.mitigation_due_date || "" },
    }));
  };

  const saveMitigation = async (id) => {
    const draft = mitigationDrafts[id] || { mitigation_plan: "", mitigation_due_date: "" };
    const { data } = await api.patch(`/raid/${id}`, {
      mitigation_plan: draft.mitigation_plan,
      mitigation_due_date: draft.mitigation_due_date || null,
    });
    setItems((p) => p.map((it) => (it.raid_id === id ? data : it)));
    toast.success("Mitigation plan saved");
  };

  const addNote = async (id) => {
    const note = (noteDrafts[id] || "").trim();
    if (!note) return;
    const { data } = await api.post(`/raid/${id}/notes`, { note });
    setItems((p) => p.map((it) => (it.raid_id === id ? data : it)));
    setNoteDrafts((p) => ({ ...p, [id]: "" }));
  };

  const isOverdue = (item) => item.mitigation_due_date && item.status !== "closed" && item.mitigation_due_date < new Date().toISOString().slice(0, 10);

  const filtered = filter === "all" ? items : items.filter((i) => i.type === filter);

  // Heat map: only risks have impact × likelihood
  const IMPACTS = ["low", "medium", "high", "critical"];
  const LIKELIHOODS = ["low", "medium", "high"];
  const heatCell = (impact, likelihood) =>
    items.filter((i) => i.type === "risk" && i.impact === impact && i.likelihood === likelihood && i.status !== "closed");

  const heatColour = (impact, likelihood) => {
    const score = (IMPACTS.indexOf(impact) + 1) * (LIKELIHOODS.indexOf(likelihood) + 1);
    if (score >= 9) return "bg-red-500 border-red-600 text-white";
    if (score >= 5) return "bg-amber-200 border-amber-300 text-amber-800";
    if (score >= 3) return "bg-amber-100 border-amber-200 text-amber-700";
    return "bg-gray-100 border-gray-200 text-gray-500";
  };

  return (
    <div>
      <PageHeader
        backTo={`/workspace/${projectId}`}
        eyebrow={project ? `RAID · ${project.name}` : "RAID"}
        title="Risks, Assumptions, Issues & Dependencies"
        description="Track everything that could affect delivery — with status, owners and impact."
        actions={
          <div className="flex gap-2">
            <Link to={`/workspace/${projectId}`}>
              <Button variant="outline" className="rounded-none border-gray-300 text-gray-600 hover:text-gray-900 bg-transparent" data-testid="back-to-project-from-raid">← Project</Button>
            </Link>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="rounded-none bg-[#002FA7] hover:bg-[#002480]" data-testid="new-raid-btn">
                  <Plus className="w-4 h-4 mr-1" /> New item
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-none bg-[#111827] border-[#374151] text-white sm:max-w-lg">
                <DialogHeader><DialogTitle className="font-display tracking-tight">New RAID item</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-[11px] uppercase tracking-[0.2em]">Type</Label>
                      <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                        <SelectTrigger className="rounded-none mt-1" data-testid="raid-type-select"><SelectValue /></SelectTrigger>
                        <SelectContent>{TYPES.map((t) => <SelectItem key={t.k} value={t.k}>{t.l}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[11px] uppercase tracking-[0.2em]">Owner</Label>
                      <Input className="rounded-none mt-1" data-testid="raid-owner-input" value={form.owner} onChange={(e) => setForm({ ...form, owner: e.target.value })} />
                    </div>
                  </div>
                  <div>
                    <Label className="text-[11px] uppercase tracking-[0.2em]">Title</Label>
                    <Input className="rounded-none mt-1" data-testid="raid-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-[11px] uppercase tracking-[0.2em]">Description</Label>
                    <Textarea className="rounded-none mt-1" data-testid="raid-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label className="text-[11px] uppercase tracking-[0.2em]">Impact</Label>
                      <Select value={form.impact} onValueChange={(v) => setForm({ ...form, impact: v })}>
                        <SelectTrigger className="rounded-none mt-1" data-testid="raid-impact-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Low</SelectItem>
                          <SelectItem value="medium">Medium</SelectItem>
                          <SelectItem value="high">High</SelectItem>
                          <SelectItem value="critical">Critical</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[11px] uppercase tracking-[0.2em]">Likelihood</Label>
                      <Select value={form.likelihood} onValueChange={(v) => setForm({ ...form, likelihood: v })}>
                        <SelectTrigger className="rounded-none mt-1" data-testid="raid-likelihood-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Low</SelectItem>
                          <SelectItem value="medium">Medium</SelectItem>
                          <SelectItem value="high">High</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[11px] uppercase tracking-[0.2em]">Status</Label>
                      <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                        <SelectTrigger className="rounded-none mt-1" data-testid="raid-status-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="open">Open</SelectItem>
                          <SelectItem value="mitigating">Mitigating</SelectItem>
                          <SelectItem value="closed">Closed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-[11px] uppercase tracking-[0.2em]">Mitigation plan</Label>
                      <Textarea className="rounded-none mt-1" data-testid="raid-mitigation-plan-input" placeholder="What's the plan to reduce or resolve this?" value={form.mitigation_plan} onChange={(e) => setForm({ ...form, mitigation_plan: e.target.value })} />
                    </div>
                    <div>
                      <Label className="text-[11px] uppercase tracking-[0.2em]">Target date</Label>
                      <Input type="date" className="rounded-none mt-1" data-testid="raid-mitigation-due-input" value={form.mitigation_due_date} onChange={(e) => setForm({ ...form, mitigation_due_date: e.target.value })} />
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={create} className="rounded-none bg-[#002FA7] hover:bg-[#002480]" data-testid="confirm-create-raid">Add</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        }
      />

      <div className="px-5 sm:px-8 lg:px-12 py-8 space-y-6">
        {/* Risk Heat Map */}
        {items.some((i) => i.type === "risk") && (
          <div className="border border-[#1F2937] bg-[#0D1117] p-5">
            <div className="text-[11px] uppercase tracking-[0.25em] text-[#6B7280] mb-4">Risk heat map · open risks</div>
            <div className="overflow-x-auto">
              <table className="text-xs border-collapse">
                <thead>
                  <tr>
                    <th className="w-20 pb-2 text-right pr-3 text-[#6B7280] font-normal text-[10px]">Likelihood ↓ / Impact →</th>
                    {IMPACTS.map((imp) => (
                      <th key={imp} className="w-24 pb-2 text-center capitalize text-[10px] uppercase tracking-[0.15em] text-[#6B7280] font-normal">{imp}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[...LIKELIHOODS].reverse().map((lik) => (
                    <tr key={lik}>
                      <td className="pr-3 py-1 text-right capitalize text-[10px] text-[#6B7280]">{lik}</td>
                      {IMPACTS.map((imp) => {
                        const risks = heatCell(imp, lik);
                        return (
                          <td key={imp} className="py-1 px-1">
                            <div className={`h-14 w-24 border flex flex-col items-center justify-center rounded-sm ${heatColour(imp, lik)}`}>
                              {risks.length > 0 ? (
                                <>
                                  <span className="text-lg font-bold font-mono leading-none">{risks.length}</span>
                                  <span className="text-[9px] mt-0.5 opacity-70">{risks.length === 1 ? "risk" : "risks"}</span>
                                </>
                              ) : (
                                <span className="text-[#374151]">—</span>
                              )}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          <button onClick={() => setFilter("all")} className={`px-4 py-2 text-xs uppercase tracking-[0.2em] border ${filter === "all" ? "bg-gray-900 text-white border-gray-900" : "border-gray-300 text-gray-500 hover:text-gray-900"}`} data-testid="filter-all">All ({items.length})</button>
          {TYPES.map((t) => (
            <button key={t.k} onClick={() => setFilter(t.k)} className={`px-4 py-2 text-xs uppercase tracking-[0.2em] border ${filter === t.k ? "bg-gray-900 text-white border-gray-900" : "border-gray-300 text-gray-500 hover:text-gray-900"}`} data-testid={`filter-${t.k}`}>
              {t.l} ({items.filter((i) => i.type === t.k).length})
            </button>
          ))}
        </div>

        <div className="border border-[#1F2937] overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#030712] border-b border-[#1F2937]">
              <tr className="text-[10px] uppercase tracking-[0.2em] text-slate-400">
                <th className="p-3"></th>
                <th className="text-left p-3 font-semibold">Type</th>
                <th className="text-left p-3 font-semibold">Title</th>
                <th className="text-left p-3 font-semibold">Impact</th>
                <th className="text-left p-3 font-semibold">Likelihood</th>
                <th className="text-left p-3 font-semibold">Owner</th>
                <th className="text-left p-3 font-semibold">Mitigation due</th>
                <th className="text-left p-3 font-semibold">Status</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const typeInfo = TYPES.find((t) => t.k === r.type);
                const overdue = isOverdue(r);
                const isExpanded = expanded === r.raid_id;
                const draft = mitigationDrafts[r.raid_id] || { mitigation_plan: r.mitigation_plan || "", mitigation_due_date: r.mitigation_due_date || "" };
                return (
                  <Fragment key={r.raid_id}>
                    <tr className="border-b border-[#1F2937] hover:bg-gray-50 cursor-pointer" data-testid={`raid-row-${r.raid_id}`} onClick={() => toggleExpand(r)}>
                      <td className="p-3 text-gray-500">{isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</td>
                      <td className="p-3"><span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 ${typeInfo.color}`}>{typeInfo.l}</span></td>
                      <td className="p-3 font-medium">
                        <div>{r.title}</div>
                        {r.description && <div className="text-xs text-gray-500 mt-0.5 line-clamp-1">{r.description}</div>}
                      </td>
                      <td className="p-3 capitalize">{r.impact}</td>
                      <td className="p-3 capitalize">{r.likelihood}</td>
                      <td className="p-3 text-gray-500">{r.owner || "—"}</td>
                      <td className={`p-3 ${overdue ? "text-rose-600 font-semibold" : "text-gray-500"}`}>
                        {r.mitigation_due_date || "—"}{overdue && " · overdue"}
                      </td>
                      <td className="p-3" onClick={(e) => e.stopPropagation()}>
                        <Select value={r.status} onValueChange={(v) => updateStatus(r.raid_id, v)}>
                          <SelectTrigger className="rounded-none h-8 w-32 text-xs" data-testid={`raid-status-${r.raid_id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="open">Open</SelectItem>
                            <SelectItem value="mitigating">Mitigating</SelectItem>
                            <SelectItem value="closed">Closed</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-3 text-right" onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => remove(r.raid_id)} className="text-gray-400 hover:text-red-600" data-testid={`delete-raid-${r.raid_id}`}>
                          <X className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="border-b border-[#1F2937] bg-[#030712]">
                        <td colSpan={9} className="p-4">
                          <div className="grid md:grid-cols-2 gap-6">
                            <div className="space-y-3">
                              <div className="text-[11px] uppercase tracking-[0.2em] text-[#6B7280]">Mitigation plan</div>
                              <Textarea
                                className="rounded-none"
                                data-testid={`raid-mitigation-plan-edit-${r.raid_id}`}
                                placeholder="What's the plan to reduce or resolve this?"
                                value={draft.mitigation_plan}
                                onChange={(e) => setMitigationDrafts((p) => ({ ...p, [r.raid_id]: { ...draft, mitigation_plan: e.target.value } }))}
                              />
                              <div className="flex items-center gap-3">
                                <Label className="text-[11px] uppercase tracking-[0.2em] shrink-0">Target date</Label>
                                <Input
                                  type="date"
                                  className="rounded-none"
                                  data-testid={`raid-mitigation-due-edit-${r.raid_id}`}
                                  value={draft.mitigation_due_date}
                                  onChange={(e) => setMitigationDrafts((p) => ({ ...p, [r.raid_id]: { ...draft, mitigation_due_date: e.target.value } }))}
                                />
                                <Button className="rounded-none bg-[#002FA7] hover:bg-[#002480] shrink-0" data-testid={`raid-mitigation-save-${r.raid_id}`} onClick={() => saveMitigation(r.raid_id)}>Save</Button>
                              </div>
                            </div>
                            <div className="space-y-3">
                              <div className="text-[11px] uppercase tracking-[0.2em] text-[#6B7280]">Progress history</div>
                              <div className="space-y-2 max-h-40 overflow-y-auto pr-1">
                                {(r.mitigation_notes || []).length === 0 && (
                                  <div className="text-xs text-gray-500">No updates logged yet.</div>
                                )}
                                {[...(r.mitigation_notes || [])].reverse().map((n, i) => (
                                  <div key={i} className="text-xs border-l-2 border-[#374151] pl-2">
                                    <div className="text-gray-300">{n.note}</div>
                                    <div className="text-[10px] text-gray-500 mt-0.5">{n.author} · {new Date(n.created_at).toLocaleString()}</div>
                                  </div>
                                ))}
                              </div>
                              <div className="flex gap-2">
                                <Input
                                  className="rounded-none"
                                  placeholder="Log a progress update…"
                                  data-testid={`raid-note-input-${r.raid_id}`}
                                  value={noteDrafts[r.raid_id] || ""}
                                  onChange={(e) => setNoteDrafts((p) => ({ ...p, [r.raid_id]: e.target.value }))}
                                  onKeyDown={(e) => { if (e.key === "Enter") addNote(r.raid_id); }}
                                />
                                <Button className="rounded-none bg-[#002FA7] hover:bg-[#002480] shrink-0" data-testid={`raid-note-add-${r.raid_id}`} onClick={() => addNote(r.raid_id)}>Add</Button>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={9} className="p-12 text-center text-gray-500">No items yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default RaidPage;
